import { FC } from 'react';
import {
  DEFAULT_PAGINATION_PAGE_COUNT,
  SONNER_ERROR_OPTIONS,
  SONNER_SUCCESS_OPTIONS,
} from '@/common/constants';
import { Outlet, useNavigate } from 'react-router-dom';
import { AlertDialogWrapper } from '@/components/shared/AlertDialog';
import { useMutation } from '@tanstack/react-query';
import SuccessToastText from '@/components/shared/SuccessToastText';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { TableWrapper } from '@/components/shared/TableWrapper';
import * as Sentry from '@sentry/react';
import { useSuppliers } from '@/api/supplier/supplier.api';
import { deleteSupplierMutationFn } from '@/api/supplier/supplier.api';
import { Supplier, SupplierTableItem } from '@/api/supplier/supplier.model';
import { supplierTableColumns } from './utils/SupplierTableColumns';
import { paths, pathsBase } from '@/routes/paths';
import useTableColumnsWithActions from '@/hooks/useTableColumnsWithActions';
import { useSearchBar } from '@/hooks/useSearchBar';
import { SearchBar } from '@/components/shared/SearchBar';
import { useTablePagination } from '@/hooks/useTablePagination';
import { useAlertDialog } from '@/hooks/useAlertDialog';
import useFilterAndSortByBe from '@/hooks/useFilterAndSortByBe';
import { getParamsWithAnimation } from '@/lib/utils';

const PAGE_TITLE = 'Proveedores';

const Suppliers: FC = () => {
  const navigate = useNavigate();

  const { pageIndex, pageSize, paginationState, dir, sort, setPagination, setDir, setSort } =
    useTablePagination<Supplier>();

  const { searchValue, form: searchBarForm, handleSubmit: handleSearchSubmit } = useSearchBar();

  const { data, isLoading, isFetching, refetch } = useSuppliers({
    page: pageIndex,
    size: pageSize,
    dir: dir,
    sort: sort,
    search: searchValue,
  });

  const { alertDialog, setAlertDialog, handleDismissAlertDialog } = useAlertDialog();

  const { mutate: deleteSupplier, isPending: isDeleteSupplierPending } = useMutation({
    mutationFn: deleteSupplierMutationFn,
    onSuccess: () => {
      refetch();
      toast(
        <SuccessToastText>Proveedor eliminado exitosamente</SuccessToastText>,
        SONNER_SUCCESS_OPTIONS,
      );
      handleDismissAlertDialog();
    },
    onError: (error: AxiosError) => {
      Sentry.captureException(error);
      toast.error('Ha ocurrido un error durante la eliminación del Proveedor.', {
        description: 'Por favor, intentalo nuevamente.',
        ...SONNER_ERROR_OPTIONS,
      });
    },
  });
  useFilterAndSortByBe<Supplier>(refetch, setDir, setSort);

  const handleEditSupplier = (supplier: SupplierTableItem) =>
    navigate(
      `${paths.SUPPLIERS}/${pathsBase.EDIT}/${supplier.id}${getParamsWithAnimation(location.search)}`,
    );

  const handleDeleteSupplier = (supplier: SupplierTableItem) => {
    setAlertDialog({
      title: `¿Estás seguro que queres eliminar "${supplier.name}"? `,
      description: `Esta acción no puede ser deshecha y se perderá toda la información asociada a este Proveedor: "${supplier.name}". `,
      open: true,
      onCancel: handleDismissAlertDialog,
      onConfirm: () => deleteSupplier(supplier.id),
    });
  };

  const suppliersColumnsWithActions = useTableColumnsWithActions<Supplier>(
    supplierTableColumns,
    handleEditSupplier,
    handleDeleteSupplier,
  );

  return (
    <>
      <SearchBar
        form={searchBarForm}
        searchTerm="proveedor"
        isLoading={isLoading || isFetching}
        className="right-[204px]"
        submitHandler={handleSearchSubmit}
      />
      <TableWrapper
        columns={suppliersColumnsWithActions}
        data={data ? data[0] : data}
        isLoading={isLoading}
        isFetching={isFetching}
        rowsLabel={PAGE_TITLE}
        pageCount={data ? data[0].totalPages : DEFAULT_PAGINATION_PAGE_COUNT}
        pagination={paginationState}
        onPaginationChange={setPagination}
      />
      <AlertDialogWrapper
        {...alertDialog}
        isLoading={isDeleteSupplierPending}
        onDismiss={handleDismissAlertDialog}
      />
      <Outlet />
    </>
  );
};

export default Suppliers;
